import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { utcToChileanInputDate, chileanInputDateToISO, dateToChileanString, getCurrentChileanDate } from '../../utils/dateUtils';

export const EditEmployeeDialog = ({ employee, open, onOpenChange, onEmployeeUpdated }) => {
  const [formData, setFormData] = useState({
    name: '',
    rut: '',
    position: '',
    startDate: '',
    legalDays: 15,
    additionalDays: 0,
    administrativeDays: 3,
    usedDays: 0
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (employee) {
      // Usar la utilidad para convertir correctamente la fecha UTC a fecha chilena
      const startDateStr = employee.startDate || employee.start_date;
      const chileanStartDate = utcToChileanInputDate(startDateStr);
      
      setFormData({
        name: employee.name || '',
        rut: employee.rut || '',
        position: employee.position || '',
        startDate: chileanStartDate,
        legalDays: employee.legalDays || employee.legal_days || 15,
        additionalDays: employee.additionalDays || employee.additional_days || 0,
        administrativeDays: employee.administrativeDays || employee.administrative_days || 3,
        usedDays: employee.usedDays || employee.used_days || 0
      });
    }
  }, [employee]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      toast.error('El nombre es obligatorio');
      return false;
    }
    if (!formData.position.trim()) {
      toast.error('El cargo es obligatorio');
      return false;
    }
    if (!formData.startDate) {
      toast.error('La fecha de inicio es obligatoria');
      return false;
    }
    if (new Date(formData.startDate) > new Date()) {
      toast.error('La fecha de inicio no puede ser futura');
      return false;
    }
    if (formData.rut && formData.rut.trim()) {
      const rutRegex = /^[0-9]{1,2}\.[0-9]{3}\.[0-9]{3}-[0-9kK]$/;
      if (!rutRegex.test(formData.rut)) {
        toast.error('Formato de RUT inválido. Use: XX.XXX.XXX-X');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      const updates = {
        name: formData.name.trim(),
        rut: formData.rut.trim() || null,
        position: formData.position.trim(),
        startDate: chileanInputDateToISO(formData.startDate),
        legalDays: parseInt(formData.legalDays) || 15,
        additionalDays: parseInt(formData.additionalDays) || 0,
        administrativeDays: parseInt(formData.administrativeDays) || 3,
        usedDays: parseFloat(formData.usedDays) || 0
      };

      await onEmployeeUpdated?.(employee.id || employee._id, updates);
      toast.success('Empleado actualizado exitosamente');
      onOpenChange(false);
    } catch (error) {
      console.error('Error updating employee:', error);
      toast.error('Error al actualizar empleado: ' + (error.message || 'Error desconocido'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Empleado</DialogTitle>
        </DialogHeader>
        
        {employee && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nombre Completo *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Nombre completo del empleado"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="rut">RUT (Opcional)</Label>
                <Input
                  id="rut"
                  value={formData.rut}
                  onChange={(e) => handleChange('rut', e.target.value)}
                  placeholder="XX.XXX.XXX-X"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="position">Cargo *</Label>
                <Input
                  id="position"
                  value={formData.position}
                  onChange={(e) => handleChange('position', e.target.value)}
                  placeholder="Cargo del empleado"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="startDate">Fecha de Inicio *</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => handleChange('startDate', e.target.value)}
                  max={dateToChileanString(getCurrentChileanDate())}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="legalDays">Días Legales</Label>
                <Input
                  id="legalDays"
                  type="number"
                  min="15"
                  max="30"
                  value={formData.legalDays}
                  onChange={(e) => handleChange('legalDays', e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Días de vacaciones por ley (15-30)
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="additionalDays">Días Adicionales</Label>
                <Input
                  id="additionalDays"
                  type="number"
                  min="0"
                  value={formData.additionalDays}
                  onChange={(e) => handleChange('additionalDays', e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Días extras otorgados por la empresa
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="administrativeDays">Días Administrativos</Label>
                <Input
                  id="administrativeDays"
                  type="number"
                  min="0"
                  max="10"
                  value={formData.administrativeDays}
                  onChange={(e) => handleChange('administrativeDays', e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Días administrativos anuales (0-10)
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="usedDays">Días Ya Utilizados</Label>
                <Input
                  id="usedDays"
                  type="number"
                  min="0"
                  step="0.5"
                  value={formData.usedDays}
                  onChange={(e) => handleChange('usedDays', e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Días de vacaciones ya tomados
                </p>
              </div>
            </div>

            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
              <h4 className="font-medium text-blue-900 mb-2">Información Importante:</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Los días acumulados se recalculan automáticamente</li>
                <li>• Los días progresivos se configuran por separado</li>
                <li>• Los días administrativos se resetean cada año</li>
              </ul>
            </div>
            
            <div className="flex justify-end space-x-2 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                disabled={loading}
              >
                {loading ? 'Guardando...' : 'Guardar Cambios'}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
